(() => {
  const DIALOG_ID = 'lre-personalization-required-dialog';

  const getDialog = () => document.getElementById(DIALOG_ID);

  const isQuickAddContext = (context) =>
    Boolean(context?.closest?.('#quick-add-dialog'));

  const getProductScope = (context) => {
    if (context?.closest) {
      const scoped =
        context.closest('.product-information') ||
        context.closest('product-component') ||
        context.closest('[data-product-grid-content]');
      if (scoped) return scoped;
    }

    return (
      document.querySelector('#MainContent .product-information') ||
      document.querySelector('#MainContent product-component') ||
      document.querySelector('#MainContent')
    );
  };

  const requiredSelector =
    '[data-lre-personalization-field][required], input[name^="properties["][required], textarea[name^="properties["][required], select[name^="properties["][required]';

  const getRequiredPersonalizationFields = (context) => {
    if (!context || isQuickAddContext(context)) return [];

    const scope = getProductScope(context);
    if (!scope) return [];

    const fields = new Set();

    scope.querySelectorAll(requiredSelector).forEach((field) => fields.add(field));

    return [...fields].filter(
      (field) =>
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement ||
        field instanceof HTMLSelectElement
    );
  };

  const fieldIsComplete = (field, context) => {
    if (field.disabled || field.closest('[hidden]')) return true;

    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return field.checked;
    }

    if (field instanceof HTMLInputElement && field.type === 'radio') {
      if (!field.name) return field.checked;

      const scope = getProductScope(context || field);
      if (!scope) return field.checked;

      return [...scope.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(field.name)}"]`
      )].some((candidate) => !candidate.disabled && candidate.checked);
    }

    return String(field.value || '').trim().length > 0;
  };

  const personalizationState = (context) => {
    if (isQuickAddContext(context)) {
      return { complete: false, fields: [], incomplete: [] };
    }

    const fields = getRequiredPersonalizationFields(context);
    const incomplete = fields.filter((field) => !fieldIsComplete(field, context));

    return {
      complete: fields.length > 0 && incomplete.length === 0,
      fields,
      incomplete
    };
  };

  const getPropertyLabel = (field) => {
    const wrapper = field.closest('product-custom-property-component, .spacing-style');
    const heading = wrapper?.querySelector?.('.__heading');
    const headingText = heading?.textContent?.replace(/\s*\*\s*$/, '').trim();

    if (headingText) return headingText;

    const propertyMatch = field.name?.match(/^properties\[(.+)\]$/);
    if (propertyMatch?.[1]) return propertyMatch[1];

    if (field.id) {
      const explicit = document.querySelector(
        `label[for="${CSS.escape(field.id)}"]`
      )?.textContent?.trim();
      if (explicit) return explicit.replace(/\s*\*\s*$/, '').trim();
    }

    return 'Required personalization';
  };

  const uniqueIncompleteLabels = (fields) => {
    const labels = [];
    const seen = new Set();

    fields.forEach((field) => {
      const label = getPropertyLabel(field);
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    });

    return labels;
  };

  const getPropertyValue = (propertyKey, context) => {
    if (!propertyKey) return '';

    const scope = getProductScope(context);
    if (!scope) return '';

    const name = `properties[${propertyKey}]`;
    const fields = [...scope.querySelectorAll(
      `[name="${CSS.escape(name)}"]`
    )];

    if (fields.length === 0) return '';

    const radio = fields.find(
      (field) => field instanceof HTMLInputElement && field.type === 'radio' && field.checked
    );
    if (radio) return radio.value;

    const checkbox = fields.find(
      (field) => field instanceof HTMLInputElement && field.type === 'checkbox'
    );
    if (checkbox) return checkbox.checked ? checkbox.value : '';

    return String(fields[0]?.value || '').trim();
  };

  const syncConditionalFields = (context = document) => {
    const scope = getProductScope(context);
    if (!scope) return;

    scope.querySelectorAll('[data-lre-conditional-property]').forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;

      const propertyKey = wrapper.dataset.lreConditionalProperty || '';
      const expectedValue = wrapper.dataset.lreConditionalValue || '';
      const shouldShow = getPropertyValue(propertyKey, wrapper) === expectedValue;
      const requiredWhenVisible = wrapper.dataset.lreRequiredWhenVisible === 'true';

      wrapper.hidden = !shouldShow;

      wrapper.querySelectorAll(
        '[data-lre-personalization-field], input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
      ).forEach((field) => {
        if (
          !(field instanceof HTMLInputElement) &&
          !(field instanceof HTMLTextAreaElement) &&
          !(field instanceof HTMLSelectElement)
        ) return;

        field.disabled = !shouldShow;

        if (requiredWhenVisible) {
          field.required = shouldShow;
          if (shouldShow) {
            field.setAttribute('aria-required', 'true');
          } else {
            field.removeAttribute('aria-required');
          }
        }
      });
    });
  };

  const firstIncompleteField = (context) =>
    personalizationState(context).incomplete[0] || null;

  const showGate = (context) => {
    const dialog = getDialog();
    if (!(dialog instanceof HTMLDialogElement)) return;

    syncConditionalFields(context);
    const state = personalizationState(context);
    const labels = uniqueIncompleteLabels(state.incomplete);

    dialog._lreContext = context || null;

    const list = dialog.querySelector('[data-lre-missing-fields]');
    const wrap = dialog.querySelector('[data-lre-missing-fields-wrap]');

    if (list instanceof HTMLElement) {
      list.replaceChildren();

      labels.forEach((label) => {
        const item = document.createElement('li');
        item.textContent = label;
        list.appendChild(item);
      });
    }

    if (wrap instanceof HTMLElement) {
      wrap.hidden = labels.length === 0;
    }

    if (!dialog.open) dialog.showModal();
  };

  const syncPaymentGates = () => {
    document.querySelectorAll(
      '.lre-personalized-accelerated-checkout[data-lre-personalized="true"]'
    ).forEach((wrapper) => {
      const gate = wrapper.querySelector('.lre-personalization-payment-gate');
      if (!(gate instanceof HTMLElement)) return;

      syncConditionalFields(wrapper);
      gate.hidden = personalizationState(wrapper).complete;
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const quickAdd = target.closest(
        '#quick-add-dialog product-form-component[data-lre-personalized="true"] .add-to-cart-button'
      );

      if (quickAdd) {
        const component = quickAdd.closest(
          'product-form-component[data-lre-personalized="true"]'
        );

        event.preventDefault();
        event.stopImmediatePropagation();
        showGate(component || quickAdd);
        return;
      }

      const mainAdd = target.closest(
        'product-form-component[data-lre-personalized="true"] [ref="addToCartButton"]'
      );

      if (mainAdd) {
        const component = mainAdd.closest(
          'product-form-component[data-lre-personalized="true"]'
        );

        syncConditionalFields(component || mainAdd);
        const state = personalizationState(component || mainAdd);

        if (!state.complete) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showGate(component || mainAdd);
          return;
        }
      }

      const trigger = target.closest('[data-lre-personalization-gate-trigger]');
      if (trigger) {
        syncConditionalFields(trigger);
        const state = personalizationState(trigger);

        if (state.complete) {
          trigger.hidden = true;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        showGate(trigger);
        return;
      }

      const close = target.closest('[data-lre-personalization-close]');
      if (close) {
        event.preventDefault();
        getDialog()?.close();
        return;
      }

      const cta = target.closest('[data-lre-personalization-cta]');
      if (cta) {
        event.preventDefault();

        const dialog = getDialog();
        const context = dialog?._lreContext;
        if (!context) {
          dialog?.close();
          return;
        }

        syncConditionalFields(context);
        const incomplete = firstIncompleteField(context);

        dialog?.close();

        if (incomplete) {
          incomplete.scrollIntoView({ behavior: 'smooth', block: 'center' });
          window.setTimeout(() => {
            if (typeof incomplete.focus === 'function') {
              incomplete.focus({ preventScroll: true });
            }
          }, 350);
        }
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      const component = form.closest(
        'product-form-component[data-lre-personalized="true"]'
      );
      if (!component) return;

      syncConditionalFields(component);
      const state = personalizationState(component);

      if (state.complete) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      showGate(component);
    },
    true
  );

  const maybeSyncFromField = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (
      target.matches('[data-lre-personalization-field]') ||
      target.matches('input[name^="properties["]') ||
      target.matches('textarea[name^="properties["]') ||
      target.matches('select[name^="properties["]')
    ) {
      syncConditionalFields(target);
      syncPaymentGates();
    }
  };

  const syncPersonalizationUi = () => {
    syncConditionalFields(document);
    syncPaymentGates();
  };

  document.addEventListener('input', maybeSyncFromField);
  document.addEventListener('change', maybeSyncFromField);
  document.addEventListener('DOMContentLoaded', syncPersonalizationUi);
  document.addEventListener('shopify:section:load', syncPersonalizationUi);

  const observer = new MutationObserver(syncPersonalizationUi);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
